import PushSheet from '@/components/PushSheet';
import { useCancelOrder } from '@/features/orders/hooks/useOrderMutations';
import { useOrderPayment } from '@/features/orders/hooks/useOrders';
import { isPaymentRefundable, paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { OnlineOrder } from '@restaurant/api-client';
import { Block, Button, List, ListItem, PageContent, Link as F7Link, f7 } from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useEffect, useRef, useState } from 'react';

/**
 * Cancel-confirmation sheet. Cancelling used to fire a bare confirm, so an owner
 * could void a PAID online order without ever being asked whether the customer
 * gets their money back. This surfaces that choice: when the payment is
 * refundable the sheet offers a refund toggle (ON by default) with a plain-words
 * line on exactly what will happen, and a red warning when they turn it off. For
 * a cash / unpaid / handoff order there's nothing to refund, so it's just the
 * normal cancel confirmation.
 *
 * Controlled by `order`: a non-null order opens the sheet. The mutation, toast
 * and refund toggle live here so both call sites (list + inspector) share one
 * cancel surface — mirrors AcceptOrderSheet.
 */
export default function CancelOrderSheet({
  order,
  from,
  onClose,
}: {
  order: OnlineOrder | null;
  /** Which pane opened this sheet - list card or inspector. */
  from: 'list' | 'inspector';
  onClose: () => void;
}) {
  const t = useT();
  const cs = t.orders.cancelSheet;
  const cancelOrder = useCancelOrder();
  const { data: payment } = useOrderPayment(order?.id ?? '');
  // Synchronous guard: the confirm is a fill Button, but a fast double-tap before
  // the sheet closes could still fire two mutations.
  const submittingRef = useRef(false);
  const [issueRefund, setIssueRefund] = useState(true);

  // Refund toggle defaults ON every time a new order opens.
  useEffect(() => {
    setIssueRefund(true);
  }, [order]);

  // Cancelling a COMPLETED (cash) order is the counter-correction rail — it keeps
  // its own long wording (reverses money / points / the visit count), matching
  // the list + inspector.
  const isUndo = order?.status === 'COMPLETED';
  const copy = isUndo ? t.orders.action.undoCompletion : t.orders.action.cancelOrder;

  const refundable = isPaymentRefundable(payment);
  const remainingRefundable = payment ? payment.amountCents - payment.refundedCents : 0;
  const providerLabel = payment ? paymentMethodLabel(payment.methodKind, t.orders.methodKind) : '';
  const amount = order && payment ? formatMoney(remainingRefundable, order.currency) : '';

  const submit = () => {
    if (!order || submittingRef.current) return;
    submittingRef.current = true;
    cancelOrder.mutate(
      {
        orderId: order.id,
        issueRefund: refundable ? issueRefund : undefined,
        track: { action: 'reject', order, from },
      },
      {
        onSuccess: () => {
          f7.sheet.close();
          f7.toast.show({ text: copy.success, closeTimeout: 2000, position: 'bottom' });
        },
        onError: (e) =>
          f7.toast.show({
            text: e instanceof Error ? e.message : cs.failed,
            closeTimeout: 3000,
            position: 'bottom',
          }),
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  return (
    <PushSheet open={!!order} onClose={onClose} style={{ height: 'auto' }}>
      {/* Custom header: cancel / centered title / spacer on a 3-column grid — same
          layout as AcceptOrderSheet so the two sheets read as one family. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          padding: '18px 16px 14px',
          background: 'var(--f7-page-bg-color)',
          borderBottom: '1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.1))',
        }}
      >
        <F7Link sheetClose style={{ justifySelf: 'start', color: 'var(--f7-theme-color)' }}>
          {cs.keep}
        </F7Link>
        <span style={{ fontSize: 17, fontWeight: 600 }}>{copy.title}</span>
        <span />
      </div>
      <PageContent style={{ background: 'var(--f7-page-bg-color)' }}>
        {order && (
          <>
            {refundable ? (
              <>
                <List strongIos insetIos>
                  <ListItem title={cs.refundToggle}>
                    <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
                      arrow_uturn_left
                    </i>
                    <ToggleSwitch
                      slot="after"
                      data-testid="cancel-refund-toggle"
                      checked={issueRefund}
                      onChange={setIssueRefund}
                      color="green"
                    />
                  </ListItem>
                </List>
                <Block>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.4,
                      color: issueRefund ? 'var(--f7-text-color-secondary, #8e8e93)' : '#ff3b30',
                      fontWeight: issueRefund ? 400 : 600,
                    }}
                  >
                    {issueRefund ? cs.refundLine(amount, providerLabel) : cs.noRefundWarning}
                  </p>
                </Block>
              </>
            ) : (
              <Block>
                <p
                  style={{
                    margin: 0,
                    fontSize: 15,
                    lineHeight: 1.4,
                    color: 'var(--f7-text-color, #000)',
                  }}
                >
                  {copy.confirm}
                </p>
              </Block>
            )}

            <Block>
              <Button
                fill
                large
                color="red"
                data-testid="cancel-confirm"
                onClick={submit}
                style={{ opacity: cancelOrder.isPending ? 0.4 : 1 }}
              >
                {copy.title}
              </Button>
            </Block>
          </>
        )}
      </PageContent>
    </PushSheet>
  );
}
